import React, { CSSProperties } from 'react';
import { AFTER_FADE, stageClass, usePlayOnce } from '../motion';

// One family growing up from its base model. Rows of labelled examples feed the stem, each bead
// in its label's colour as in the hero (billing mint, technical pink, sales amber). Each version
// trains from the one before, and the family serves a version only once it passes its gate:
// the candidate that fails branches off and never gets a number. The serving marker moves up
// the stem as each new version passes.

// One thing happens every STEP up the stem, rows on the left and the line's own events on the
// right, so the drawing keeps an even beat. The stem stops 4px short of every node, as the
// console's family line does, and the base, the rings and the serving dot share one size.
const STEP = 48;
const X = 168;
const LABEL_X = X + 24;
const BASE = 432;
const level = (k: number) => BASE - k * STEP;
const R = 7;
const DIAMOND = 8; // half the base's diagonal: a diamond this size looks as large as a ring
const HALO = 13;
const AIR = 5; // half a stroke and 4px
// The drawing's own bounds, so it keeps full-size labels from a phone up.
const VIEW = { x: -5, y: 62, w: 338, h: 401 };
const NODES = [
  { version: '@1', y: level(2) },
  { version: '@2', y: level(4) },
  { version: '@3', y: level(7) },
];
const TOP = NODES[NODES.length - 1];
type Team = 'mint' | 'pink' | 'amber';
// Times are from the start of the figure's moment; the serving marker's keyframes in
// onehot.css follow POP (0.8, 1.8 and 3.6 s).
const ROWS: { label: string; y: number; beads: Team[]; d: number }[] = [
  { label: 'dataset v1', y: level(1), beads: ['mint', 'pink', 'mint', 'amber', 'mint', 'pink'], d: 0 },
  { label: 'v2', y: level(3), beads: ['amber', 'mint', 'pink', 'mint'], d: 1.0 },
  { label: 'v3', y: level(6), beads: ['mint', 'pink', 'mint', 'amber', 'mint'], d: 2.8 },
];
const BEAD_X = X - 86;
const BEAD_STEP = 11;
const GROW = [0.3, 1.3, 3.1]; // when each stretch of the stem starts to grow
const POP = GROW.map((g) => g + 0.5);
const CANDIDATE = { x: X + 60, y: level(5), d: 2.2 };
const at = (t: number) => `${(AFTER_FADE + t).toFixed(2)}s`;

const vars = (v: Record<string, string | number>) => v as CSSProperties;

// Each stretch of stem runs between two nodes and stops short of both; the serving version's
// halo needs more room than a ring.
const STOPS = [{ y: BASE, gap: DIAMOND + AIR }, ...NODES.map((n) => ({ y: n.y, gap: (n === TOP ? HALO : R) + AIR }))];
const STRETCHES = STOPS.slice(1).map((to, i) => ({ y1: STOPS[i].y - STOPS[i].gap, y2: to.y + to.gap }));

// The failed candidate leaves @2 up and to the right, 30 degrees off the stem, from the same
// air round its ring, and reaches its own ring level.
const FROM = NODES[1];
const LEAVE = { x: X + (R + AIR) * 0.5, y: FROM.y - (R + AIR) * 0.866 };
const FAIL_PATH = `M${LEAVE.x} ${LEAVE.y} C${LEAVE.x + 6} ${LEAVE.y - 10.4} ${CANDIDATE.x - 28} ${CANDIDATE.y} ${CANDIDATE.x - R - AIR} ${CANDIDATE.y}`;

export function Lineage() {
  const [ref, stage] = usePlayOnce<SVGSVGElement>();

  return (
    <svg
      ref={ref}
      className={`oh-fig oh-lineage ${stageClass(stage)}`}
      viewBox={`${VIEW.x} ${VIEW.y} ${VIEW.w} ${VIEW.h}`}
      style={{ maxWidth: VIEW.w, marginInline: 'auto' }}
      role="img"
      aria-label="A family grows from the chub base model: support-router@1, then @2, then @3, each trained on new rows. A candidate between @2 and @3 failed its gate and never served. The family serves @3."
    >
      {ROWS.map((row) => {
        const last = BEAD_X + (row.beads.length - 1) * BEAD_STEP;
        return (
          <g key={row.label} className="oh-rows" style={vars({ '--d': at(row.d) })}>
            <text className="oh-lin-name" x={BEAD_X - 11} y={row.y + 4} textAnchor="end">{row.label}</text>
            {row.beads.map((team, k) => (
              <circle key={k} className={`oh-lin-bead is-${team}`} cx={BEAD_X + k * BEAD_STEP} cy={row.y} r="3.5" />
            ))}
            <line className="oh-feed" x1={last + 9} y1={row.y} x2={X - 2} y2={row.y} />
          </g>
        );
      })}

      {STRETCHES.map((s, i) => (
        <line
          key={s.y1}
          className="oh-stem"
          x1={X}
          y1={s.y1}
          x2={X}
          y2={s.y2}
          style={vars({ '--d': at(GROW[i]), '--len': s.y1 - s.y2 })}
        />
      ))}

      <path className="oh-base" d={`M${X} ${BASE - DIAMOND} l${DIAMOND} ${DIAMOND} l${-DIAMOND} ${DIAMOND} l${-DIAMOND} ${-DIAMOND} z`} />
      <text className="oh-lin-model" x={LABEL_X} y={BASE + 5}>chub</text>
      <text className="oh-lin-tag" x={LABEL_X} y={BASE + 25}>BASE MODEL</text>

      <g className="oh-candidate" style={vars({ '--d': at(CANDIDATE.d) })}>
        <path className="oh-fail-path" d={FAIL_PATH} />
        <circle className="oh-fail-ring" cx={CANDIDATE.x} cy={CANDIDATE.y} r={R} />
        <path className="oh-fail-cross" d={`M${CANDIDATE.x - 2.6} ${CANDIDATE.y - 2.6} l5.2 5.2 M${CANDIDATE.x + 2.6} ${CANDIDATE.y - 2.6} l-5.2 5.2`} />
        <text className="oh-lin-tag" x={CANDIDATE.x + 16} y={CANDIDATE.y + 4}>GATE FAILED</text>
      </g>

      {NODES.map((node, i) => (
        <g key={node.version} className="oh-version" style={vars({ '--d': at(POP[i]) })}>
          <circle className="oh-lin-node" cx={X} cy={node.y} r={R} />
          <text className={`oh-lin-version ${node === TOP ? 'is-current' : ''}`} x={LABEL_X} y={node.y + 7}>{node.version}</text>
          {i === 0 && <text className="oh-lin-name" x={LABEL_X} y={TOP.y - 19}>support-router</text>}
        </g>
      ))}

      <g
        className="oh-serving"
        style={vars({ '--d': at(0), '--y1': `${NODES[0].y - TOP.y}px`, '--y2': `${NODES[1].y - TOP.y}px` })}
      >
        <circle className="oh-serving-ring" cx={X} cy={TOP.y} r={HALO} />
        <circle className="oh-serving-dot" cx={X} cy={TOP.y} r={R + 1} />
        <text className="oh-lin-tag" x={LABEL_X} y={TOP.y + 27}>SERVING</text>
      </g>
    </svg>
  );
}

export default Lineage;
